import { transparentize } from "polished";
import styled, { css } from "styled-components";

import { devices, sizeMapper } from "../styles";
import { theme } from "src/styles";

export const Container = styled.div<{ fluid?: boolean; noMinHeight?: boolean }>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  ${(props) =>
    props.fluid &&
    css`
      max-width: 100%;
      min-height: 75vh;
    `}
  ${(props) =>
    props.noMinHeight &&
    css`
      min-height: 0;
    `}

  ${devices.tablet} {
    ${(props) =>
      !props.fluid &&
      css`
        padding: 0 60px;
      `}
  }

  ${devices.desktop} {
    ${(props) =>
      !props.fluid
        ? css`
            padding: 0 100px;
          `
        : css`
            min-height: 100vh;
          `}

    ${(props) =>
      props.noMinHeight &&
      css`
        min-height: 0;
      `}
  }
`;

export const Row = styled.div<{
  fullHeight?: boolean;
  hasColoredItems?: boolean;
  hasPaddingItems?: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;

  ${(props) =>
    props.fullHeight &&
    css`
      ${devices.desktop} {
        min-height: 100vh;
      }
    `}

  ${(props) =>
    props.hasPaddingItems &&
    css`
      > div {
        span {
          padding: 0 10px;

          &:last-child {
            padding-top: 5px;
          }

          &:not(:first-child) {
            &:before {
              content: "-";
              margin-right: 4px;
              font-size: 16px;
            }
          }
        }
      }
    `}


  ${(props) =>
    props.hasColoredItems &&
    css`
      > div {
        padding: 10px;
      }

      > div:nth-child(odd) {
        span {
          background: ${transparentize(0.85, theme.brandPrimary)};

          &:first-child {
            padding: 10px;
          }

          &:last-child {
            padding-bottom: 10px;
          }
        }
      }

      > div:nth-child(even) {
        span {
          &:first-child {
            padding: 0 10px 10px 10px;
          }
        }
      }
    `}
`;

interface ColumnProps {
  size?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  padding?: string;
  desktopPadding?: string;
  verticalScroll?: boolean;
}

export const Column = styled.div<ColumnProps>`
  padding: 15px;
  margin: 0;
  width: ${(props) => (props.size ? (props.size / 12) * 100 : 100)}%;
  padding: ${(props) => sizeMapper(props.padding, "small")};

  ${(props) =>
    props.sm &&
    `
  width: ${(props.sm / 12) * 100}%;`}

  ${(props) =>
    props.md &&
    css`
      ${devices.tablet} {
        width: ${(props.md / 12) * 100}%;
      }
    `}

  ${(props) =>
    props.lg &&
    css`
      ${devices.desktop} {
        width: ${(props.lg / 12) * 100}%;
      }
    `}

  ${(props) =>
    props.xl &&
    css`
      ${devices.desktopLarge} {
        width: ${(props.xl / 12) * 100}%;
      }
    `}

    ${(props) =>
    props.desktopPadding &&
    css`
      ${devices.desktop} {
        padding: ${sizeMapper(props.desktopPadding, "small")};
      }
    `}

  ${(props) =>
    props.verticalScroll &&
    css`
      overflow-y: auto;

      ${devices.tablet} {
        max-height: 100vh;
      }
    `}
`;

export const Page = styled.div`
  padding: 30px 0;
  min-height: 100vh;
`;

interface WrapperProps {
  margin?: string;
  padding?: string;
  display?: "block" | "inline-block" | "flex" | "inline-flex";
  flex?: string;
  flexDirection?: "row" | "row-reverse" | "column" | "column-reverse";
  alignItems?: "center" | "flex-start" | "flex-end";
  justifyContent?: "center" | "flex-start" | "flex-end" | "space-between";
  textAlign?: "left" | "center" | "right";
  whiteSpace?: "normal" | "nowrap" | "pre" | "pre-line" | "pre-wrap";
  flexWrap?: "wrap" | "wrap-reverse" | "revert" | "no-wrap";
  m?: number;
  mt?: number;
  mr?: number;
  mb?: number;
  ml?: number;
  p?: number;
  pt?: number;
  pr?: number;
  pb?: number;
  pl?: number;
  backgroundColor?: string;
  width?: string | number;
  height?: string | number;
  minWidth?: string | number;
  minHeight?: string | number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  borderRadius?: number | string;
  boxShadow?: string;
  cursor?: "pointer" | "default" | "not-allowed";
  overflow?: "hidden" | "visible" | "scroll" | "auto";
  textOverflow?: "clip" | "ellipsis" | "initial" | "inherit" | "unset";
  textDecoration?:
    | "dashed"
    | "dotted"
    | "double"
    | "line-through"
    | "none"
    | "overline"
    | "solid"
    | "underline"
    | "wavy";
  fontSize?: number | string;
  color?: string;
  withBorderRight?: boolean;
  withBorderLeft?: boolean;
  border?: string;
}

export const Wrapper = styled.div<WrapperProps>`
  margin: ${(props) => sizeMapper(props.margin, "no")};
  padding: ${(props) => sizeMapper(props.padding, "no")};

  .mapboxgl-ctrl-logo {
    display: none !important;
  }

  color: ${(p) => p.color || "initial"};

  ${(props) =>
    props.textAlign &&
    css`
      text-align: ${props.textAlign};
    `}

  ${(props) =>
    props.textDecoration &&
    css`
      text-decoration: ${props.textDecoration};
    `}

  ${(props) =>
    props.display &&
    css`
      display: ${props.display};
    `}

  ${(props) =>
    props.alignItems &&
    css`
      align-items: ${props.alignItems};
    `}

  ${(props) =>
    props.justifyContent &&
    css`
      justify-content: ${props.justifyContent};
    `}

  ${(props) =>
    props.whiteSpace &&
    css`
      white-space: ${props.whiteSpace};
    `}

  ${(props) =>
    props.m &&
    css`
      margin: ${props.m}px;
    `}

  ${(props) =>
    props.mt &&
    css`
      margin-top: ${props.mt}px;
    `}

  ${(props) =>
    props.mr &&
    css`
      margin-right: ${props.mr}px;
    `}

  ${(props) =>
    props.mb &&
    css`
      margin-bottom: ${props.mb}px;
    `}

  ${(props) =>
    props.ml &&
    css`
      margin-left: ${props.ml}px;
    `}

  ${(props) =>
    props.p &&
    css`
      padding: ${props.p}px;
    `}

  ${(props) =>
    props.pt &&
    css`
      padding-top: ${props.pt}px;
    `}

  ${(props) =>
    props.pr &&
    css`
      padding-right: ${props.pr}px;
    `}

  ${(props) =>
    props.pb &&
    css`
      padding-bottom: ${props.pb}px;
    `}

  ${(props) =>
    props.pl &&
    css`
      padding-left: ${props.pl}px;
    `}

  ${(props) =>
    props.flexWrap &&
    css`
      flex-wrap: ${props.flexWrap};
    `}

  ${(props) =>
    props.flex &&
    css`
      flex: ${props.flex};
    `}

  ${(props) =>
    props.flexDirection &&
    css`
      flex-direction: ${props.flexDirection};
    `}

  ${(props) =>
    props.backgroundColor &&
    css`
      background-color: ${props.backgroundColor};
    `}

  ${(props) =>
    props.width &&
    css`
      width: ${typeof props.width === "number" ? `${props.width}px` : props.width};
    `}

  ${(props) =>
    props.height &&
    css`
      height: ${typeof props.height === "number" ? `${props.height}px` : props.height};
    `}

  ${(props) =>
    props.minWidth &&
    css`
      min-width: ${typeof props.minWidth === "number" ? `${props.minWidth}px` : props.minWidth};
    `}

  ${(props) =>
    props.minHeight &&
    css`
      min-height: ${typeof props.minHeight === "number" ? `${props.minHeight}px` : props.minHeight};
    `}

    ${(props) =>
    props.maxWidth &&
    css`
      max-width: ${typeof props.maxWidth === "number" ? `${props.maxWidth}px` : props.maxWidth};
    `}

  ${(props) =>
    props.maxHeight &&
    css`
      max-height: ${typeof props.maxHeight === "number" ? `${props.maxHeight}px` : props.maxHeight};
    `}

  ${(props) =>
    props.borderRadius &&
    css`
      border-radius: ${typeof props.borderRadius === "number"
        ? `${props.borderRadius}px`
        : props.borderRadius};
    `}

  ${(props) =>
    props.boxShadow &&
    css`
      box-shadow: ${props.boxShadow};
    `}

  ${(props) =>
    props.cursor &&
    css`
      cursor: ${props.cursor};
    `}

  ${(props) =>
    props.overflow &&
    css`
      overflow: ${props.overflow};
    `}

  ${(props) =>
    props.textOverflow &&
    css`
      text-overflow: ${props.textOverflow};
    `}

  ${(props) =>
    props.fontSize &&
    css`
      font-size: ${typeof props.fontSize === "number" ? `${props.fontSize}px` : props.fontSize};
    `}

    ${(props) =>
    props.withBorderRight &&
    css`
      border-right: 1px solid ${(props) => props.theme.grayLight};
    `}

    ${(props) =>
    props.withBorderLeft &&
    css`
      border-left: 1px solid ${(props) => props.theme.grayLight};
    `}

    ${(props) =>
    props.border &&
    css`
      border: ${props.border};
    `}
`;
